/**
 * Debounce utility to prevent excessive API calls (e.g., on keystroke)
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, delay = 300) {
  let timeoutId = null;

  return function debounced(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Request deduplication helper - merges concurrent identical requests
 * @param {Function} requestFn - Async function that makes the request
 * @returns {Function} Wrapper that deduplicates requests
 */
export function createDedupedRequest(requestFn) {
  let pendingRequest = null;

  return async function dedupedRequest(...args) {
    // If a request with same args is already pending, return that promise
    if (pendingRequest) {
      return pendingRequest;
    }

    // Make new request and store the promise
    pendingRequest = requestFn(...args);

    try {
      const result = await pendingRequest;
      return result;
    } finally {
      pendingRequest = null;
    }
  };
}

/**
 * Throttle utility - limits function calls to once per interval
 * @param {Function} func - Function to throttle
 * @param {number} interval - Interval in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, interval = 1000) {
  let lastCallTime = 0;
  let timeoutId = null;

  return function throttled(...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;

    if (timeSinceLastCall >= interval) {
      func(...args);
      lastCallTime = now;
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        func(...args);
        lastCallTime = Date.now();
        timeoutId = null;
      }, interval - timeSinceLastCall);
    }
  };
}
